import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAwsCognitoActions } from '../../contexts/AWSAuthContext';

const THEME = '#26256b';
const THEME_LIGHT = '#3230a0';

// Two steps: ask Cognito to email a verification code, then use the code to
// choose a new password. `startAtCode` opens on the second step for a user
// whose password an administrator has already reset.
const ForgotPasswordModal = ({ isOpen, onClose, onDone, initialEmail = '', startAtCode = false }) => {
  const { forgotPassword, confirmForgotPassword } = useAwsCognitoActions();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(startAtCode ? 'confirm' : 'request');
    setEmail(initialEmail);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setError('');
    setInfo(
      startAtCode
        ? 'Your password was reset by an administrator. Enter the code that was emailed to you.'
        : '',
    );
  }, [isOpen, initialEmail, startAtCode]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  const sendCode = async () => {
    setError('');
    if (!email.trim()) {
      setError('Enter your work email.');
      return;
    }
    setLoading(true);
    const result = await forgotPassword(email.trim());
    setLoading(false);
    if (result?.success) {
      setInfo(`If ${email.trim()} has an account, a verification code has been emailed to it.`);
      setStep('confirm');
    } else {
      setError(result?.error || 'Could not send a verification code. Try again.');
    }
  };

  const handleRequest = (e) => {
    e.preventDefault();
    sendCode();
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Enter your work email.');
      return;
    }
    if (!code.trim()) {
      setError('Enter the verification code from the email.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setError('Password must contain uppercase, lowercase, number, and special character');
      return;
    }

    setLoading(true);
    const result = await confirmForgotPassword(email.trim(), code.trim(), newPassword);
    setLoading(false);
    if (result?.success) {
      onDone(email.trim());
    } else {
      setError(result?.error || 'Could not reset the password. Check the code and try again.');
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '12px 14px',
    border: `1.5px solid ${focusedField === field ? THEME : '#d1d5db'}`,
    borderRadius: 10,
    outline: 'none',
    fontSize: 14,
    color: '#1f2937',
    background: '#fff',
    transition: 'border-color 0.2s',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
  });

  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 };

  const linkStyle = {
    background: 'none', border: 'none', padding: 0, cursor: loading ? 'not-allowed' : 'pointer',
    color: THEME_LIGHT, fontSize: 13, fontWeight: 600, fontFamily: "'Inter', sans-serif",
  };

  const field = (name, props) => (
    <input
      {...props}
      onFocus={() => setFocusedField(name)}
      onBlur={() => setFocusedField(null)}
      style={inputStyle(name)}
      disabled={loading}
    />
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            style={{
              position: 'relative',
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 8px 40px rgba(38,37,107,0.18)',
              width: '100%',
              maxWidth: 440,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              padding: 36,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              disabled={loading}
              aria-label="Close"
              style={{
                position: 'absolute', top: 14, right: 14,
                background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                color: '#9ca3af', opacity: loading ? 0.5 : 1, padding: 6, borderRadius: 6,
              }}
            >
              <XMarkIcon style={{ width: 20, height: 20 }} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                margin: '0 auto 16px',
                width: 60, height: 60,
                background: THEME,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 16px ${THEME}55`,
              }}>
                <KeyIcon style={{ width: 28, height: 28, color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: '0 0 6px' }}>
                {step === 'request' ? 'Forgot password' : 'Choose a new password'}
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                {step === 'request'
                  ? 'Enter your work email and we will send you a verification code.'
                  : 'Enter the verification code from the email and your new password.'}
              </p>
            </div>

            {info && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                background: `${THEME}0d`, border: `1px solid ${THEME}33`,
                fontSize: 13, color: THEME, lineHeight: 1.45,
              }}>
                {info}
              </div>
            )}

            <form
              onSubmit={step === 'request' ? handleRequest : handleConfirm}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <div>
                <label htmlFor="forgotEmail" style={labelStyle}>Work email</label>
                {field('email', {
                  id: 'forgotEmail',
                  type: 'email',
                  value: email,
                  onChange: (e) => { setEmail(e.target.value); setError(''); },
                  placeholder: 'you@company.com',
                  autoComplete: 'username',
                  required: true,
                })}
              </div>

              {step === 'confirm' && (
                <>
                  <div>
                    <label htmlFor="forgotCode" style={labelStyle}>Verification code</label>
                    {field('code', {
                      id: 'forgotCode',
                      type: 'text',
                      inputMode: 'numeric',
                      value: code,
                      onChange: (e) => { setCode(e.target.value); setError(''); },
                      placeholder: 'Code from the email',
                      autoComplete: 'one-time-code',
                      required: true,
                    })}
                  </div>

                  <div>
                    <label htmlFor="forgotNewPassword" style={labelStyle}>New password</label>
                    <div style={{ position: 'relative' }}>
                      {field('newPassword', {
                        id: 'forgotNewPassword',
                        type: showPassword ? 'text' : 'password',
                        value: newPassword,
                        onChange: (e) => { setNewPassword(e.target.value); setError(''); },
                        placeholder: 'Enter new password',
                        autoComplete: 'new-password',
                        required: true,
                      })}
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0,
                        }}
                      >
                        {showPassword
                          ? <EyeSlashIcon style={{ width: 18, height: 18 }} />
                          : <EyeIcon style={{ width: 18, height: 18 }} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="forgotConfirmPassword" style={labelStyle}>Confirm password</label>
                    {field('confirmPassword', {
                      id: 'forgotConfirmPassword',
                      type: showPassword ? 'text' : 'password',
                      value: confirmPassword,
                      onChange: (e) => { setConfirmPassword(e.target.value); setError(''); },
                      placeholder: 'Confirm new password',
                      autoComplete: 'new-password',
                      required: true,
                    })}
                  </div>

                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
                    At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character.
                  </p>
                </>
              )}

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="alert"
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#fef2f2',
                    border: '1.5px solid #fca5a5',
                    borderRadius: 8,
                    fontSize: 13,
                    color: '#dc2626',
                    lineHeight: 1.45,
                  }}
                >
                  {error}
                </motion.div>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px 16px',
                    border: `1.5px solid #d1d5db`,
                    background: '#fff',
                    color: '#374151',
                    borderRadius: 10,
                    fontWeight: 500,
                    fontSize: 14,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px 16px',
                    background: THEME,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: `0 4px 14px ${THEME}55`,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {step === 'request'
                    ? (loading ? 'Sending…' : 'Send code')
                    : (loading ? 'Resetting…' : 'Reset password')}
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {step === 'request' ? (
                  <button type="button" disabled={loading} style={linkStyle}
                    onClick={() => { setError(''); setInfo(''); setStep('confirm'); }}>
                    I already have a code
                  </button>
                ) : (
                  <button type="button" disabled={loading} style={linkStyle} onClick={sendCode}>
                    Send a new code
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;
